import { Cloud, CloudOff, RefreshCw, Loader2 } from 'lucide-react';
import { useContext } from 'react';
import { DataContext } from '../context/DataContext';

/**
 * SyncStatusIndicator — Mostra o status da sincronização com o backend.
 * Usa exclusivamente variáveis CSS do design system.
 * Resiliente: renderiza fallback se DataContext não estiver disponível.
 */
export function SyncStatusIndicator() {
  const ctx = useContext(DataContext);

  // Graceful fallback when rendered outside DataProvider (e.g. during HMR)
  if (!ctx) {
    return (
      <div
        className="flex items-center gap-2 px-3 py-2"
        style={{
          backgroundColor: 'color-mix(in srgb, var(--sidebar-border) 50%, transparent)',
          borderRadius: 'var(--radius)',
          fontFamily: 'var(--font-family-inter)',
        }}
      >
        <Cloud size={14} style={{ color: 'var(--muted-foreground)' }} />
        <span
          style={{
            fontSize: 'var(--text-xs)',
            fontWeight: 'var(--font-weight-medium)',
            color: 'var(--sidebar-foreground)',
          }}
        >
          Inicializando...
        </span>
      </div>
    );
  }

  const { syncStatus, isOnline, lastSync, refreshSync } = ctx;

  const statusConfig = {
    idle: { icon: Cloud, color: 'var(--muted-foreground)', label: 'Aguardando...' },
    syncing: { icon: Loader2, color: 'var(--primary)', label: 'Sincronizando...' },
    synced: { icon: Cloud, color: 'var(--success)', label: 'Sincronizado' },

    error: { icon: CloudOff, color: 'var(--error)', label: 'Offline' },
  };

  const config = statusConfig[syncStatus];
  const Icon = config.icon;
  const isAnimating = syncStatus === 'syncing';

  const formatLastSync = (ts: string | null) => {
    if (!ts) return '';
    const d = new Date(ts);
    return d.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' });
  };

  return (
    <div
      className="flex items-center gap-2 px-3 py-2"
      style={{
        backgroundColor: 'color-mix(in srgb, var(--sidebar-border) 50%, transparent)',
        borderRadius: 'var(--radius)',
        fontFamily: 'var(--font-family-inter)',
      }}
    >
      <div
        className="flex items-center justify-center"
        style={{
          color: config.color,
          animation: isAnimating ? 'spin 1s linear infinite' : 'none',
        }}
      >
        <Icon size={14} />
      </div>
      <div className="flex-1 min-w-0">
        <span
          style={{
            fontSize: 'var(--text-xs)',
            fontWeight: 'var(--font-weight-medium)',
            color: 'var(--sidebar-foreground)',
            display: 'block',
            lineHeight: '1.3',
          }}
        >
          {config.label}
        </span>
        {lastSync && syncStatus === 'synced' && (
          <span
            style={{
              fontSize: 'var(--text-micro)',
              color: 'var(--muted-foreground)',
              display: 'block',
              lineHeight: '1.3',
            }}
          >
            {formatLastSync(lastSync)}
          </span>
        )}
      </div>
      {syncStatus === 'error' && (
        <button
          onClick={refreshSync}
          className="flex items-center justify-center p-1 transition-opacity hover:opacity-70"
          style={{
            background: 'transparent',
            border: 'none',
            cursor: 'pointer',
            color: 'var(--muted-foreground)',
          }}
          title="Tentar reconectar"
        >
          <RefreshCw size={12} />
        </button>
      )}
    </div>
  );
}